import { Center, Group, SimpleGrid, Stack, Title } from "@mantine/core"
import { useDisclosure, usePagination } from "@mantine/hooks"
import { CertificateRequestStatus } from "@tfg/db/browser"
import { Pagination, ErrorBox, Loader } from "@tfg/ui"
import { SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { removeUndefined } from "@tfg/types/schemas/utils"

import { queryClient } from "~/backend/queryClient"
import {
  FindCertificateRequestsForSupplierQueryParams,
  useFindCertificateRequestsForSupplier,
  CertificateTypeDtoCategoryEnumKey,
  getCertificateRequestsForSupplierStatsQueryKey,
  findCertificateRequestsForSupplierQueryKey,
} from "~/gen/api"
import { TransferCertificateRequestModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/TransferCertificateRequestModal.tsx"
import { CertificateRequestsBatchModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/CertificateRequestsBatchModal"
import { CertificateRequest } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequest"
import { CertificateRequestStatusFilter } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestStatusFilter"

const REQUESTS_PER_PAGE = 10

export const CertificateRequests = ({
  category,
}: {
  category: CertificateTypeDtoCategoryEnumKey
}) => {
  const { t } = useTranslation(["traceability"])

  const [params, setParams] =
    useState<FindCertificateRequestsForSupplierQueryParams>({
      status: CertificateRequestStatus.OPENED,
      category,
    })

  const {
    data: certificateRequests,
    isPending: isLoading,
    isError,
  } = useFindCertificateRequestsForSupplier(removeUndefined(params))

  const requestsTotal = certificateRequests?.data?.length ?? 0
  const pagination = usePagination({
    total: Math.ceil(requestsTotal / REQUESTS_PER_PAGE),
  })

  const onChangeParams = (
    params: SetStateAction<FindCertificateRequestsForSupplierQueryParams>
  ) => {
    setParams(params)
    pagination.first()
  }

  const [transferCertificateRequestIds, setTransferCertificateRequestIds] =
    useState<string[]>()
  const [
    openedTransferModal,
    { open: openTransferModal, close: closeTransferModal },
  ] = useDisclosure(false)

  return (
    <Stack>
      <Group justify="space-between">
        <CertificateRequestStatusFilter
          params={params}
          setParams={onChangeParams}
        />
        <Pagination
          value={pagination.active}
          onChange={pagination.setPage}
          total={Math.ceil(requestsTotal / REQUESTS_PER_PAGE)}
        />
      </Group>
      {requestsTotal === 0 ? (
        <Center pt="xl">
          {params.status === CertificateRequestStatus.OPENED ? (
            <Title order={3}>{t("certificates.noRequests.open")}</Title>
          ) : (
            <Title order={3}>{t("certificates.noRequests.closed")}</Title>
          )}
        </Center>
      ) : (
        <Stack>
          {isLoading ? (
            <Loader fullPage />
          ) : isError ? (
            <ErrorBox title={t("request.errors.generic")} error={null} />
          ) : (
            <SimpleGrid
              cols={{ base: 1, sm: 2, lg: 3 }}
              spacing="md"
              style={{ alignItems: "stretch" }}
            >
              {certificateRequests?.data
                ?.slice(
                  (pagination.active - 1) * REQUESTS_PER_PAGE,
                  pagination.active * REQUESTS_PER_PAGE
                )
                .map((request) => (
                  <CertificateRequest
                    key={request.id}
                    certificateRequest={request}
                    setTransferCertificateRequestIds={
                      setTransferCertificateRequestIds
                    }
                    openTransferModal={openTransferModal}
                  />
                ))}
            </SimpleGrid>
          )}
        </Stack>
      )}
      <TransferCertificateRequestModal
        opened={openedTransferModal}
        close={() => {
          queryClient.invalidateQueries({
            queryKey: findCertificateRequestsForSupplierQueryKey(
              removeUndefined(params)
            ),
          })
          queryClient.invalidateQueries({
            queryKey: getCertificateRequestsForSupplierStatsQueryKey(),
          })
          setTransferCertificateRequestIds(undefined)
          closeTransferModal()
        }}
        certificateRequestIds={transferCertificateRequestIds}
      />
      <CertificateRequestsBatchModal />
    </Stack>
  )
}
